import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import unwrapResponse from "utils/unwrapResponse";
import { catchError } from "JS/common";
import { getSalesTags, createSalesTag, updateSalesTag, deleteSalesTag } from "JS/API";

const DEFAULT_NEW_COLOR = "#e87ba4";

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  },
  modal: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    width: "100%",
    maxWidth: "560px",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 22px",
    borderBottom: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e293b",
  },
  closeBtn: { border: "none", background: "transparent", cursor: "pointer", color: "#64748b", padding: "6px" },
  hint: { padding: "10px 22px 4px", fontSize: "12px", color: "#64748b" },
  list: { flex: 1, overflowY: "auto" },
  row: {
    display: "grid",
    gridTemplateColumns: "36px 1fr 80px auto",
    gap: "12px",
    alignItems: "center",
    padding: "10px 22px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: "13px",
  },
  colorInput: { width: "28px", height: "28px", border: "1px solid #d1d5db", borderRadius: "6px", padding: 0, cursor: "pointer", background: "none" },
  nameInput: {
    border: "1px solid transparent",
    borderRadius: "6px",
    padding: "5px 8px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#1f2937",
    fontFamily: "inherit",
    width: "100%",
    backgroundColor: "transparent",
  },
  nameInputEditing: { borderColor: "#d1d5db", backgroundColor: "#ffffff" },
  lock: { fontSize: "11px", color: "#9ca3af", marginLeft: "6px", fontWeight: "400" },
  usage: { color: "#64748b", textAlign: "right", fontVariantNumeric: "tabular-nums" },
  actions: { display: "flex", gap: "6px" },
  btn: { padding: "5px 10px", borderRadius: "6px", border: "1px solid #d1d5db", background: "#ffffff", fontSize: "12px", cursor: "pointer", fontFamily: "inherit", color: "#374151" },
  btnPrimary: { background: "#2563eb", color: "#ffffff", borderColor: "#2563eb" },
  btnDanger: { background: "#fef2f2", color: "#991b1b", borderColor: "transparent" },
  addRow: { display: "flex", gap: "8px", alignItems: "center", padding: "14px 22px", backgroundColor: "#f8fafc", borderTop: "1px solid #e2e8f0" },
  addInput: { flex: 1, border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px 10px", fontSize: "13px", fontFamily: "inherit" },
  error: { padding: "6px 22px 0", fontSize: "12px", color: "#dc2626" },
  loading: { padding: "40px", textAlign: "center", color: "#9ca3af", fontSize: "13px" },
};

const SalesTagManageModal = ({ onClose, onChanged }) => {
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: "", color: "" });
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(DEFAULT_NEW_COLOR);
  const [error, setError] = useState("");
  const [changed, setChanged] = useState(false);

  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({ mode: "", context: "", bool: false });

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getSalesTags();
      if (typeof result !== "object" || !result?.data) {
        return catchError(result, navigate, setAlertBox, setAlert);
      }
      setTags(unwrapResponse(result)?.items || []);
    } catch (e) {
      catchError(e, navigate, setAlertBox, setAlert);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const finish = useCallback(() => {
    if (changed) onChanged?.();
    onClose();
  }, [changed, onChanged, onClose]);

  const withResult = async (call) => {
    setError("");
    try {
      const result = await call();
      if (typeof result !== "object" || !result?.data) {
        catchError(result, navigate, setAlertBox, setAlert);
        return null;
      }
      setChanged(true);
      await load();
      return unwrapResponse(result);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        setError(detail);
      } else {
        catchError(err, navigate, setAlertBox, setAlert);
      }
      return null;
    }
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) {
      setError("태그 이름을 입력해 주세요.");
      return;
    }
    const created = await withResult(() => createSalesTag({ name, color: newColor }));
    if (created) {
      setNewName("");
      setNewColor(DEFAULT_NEW_COLOR);
    }
  };

  const startEdit = (tag) => {
    setEditingId(tag.id);
    setDraft({ name: tag.name, color: tag.color });
    setError("");
  };

  const saveEdit = async (tag) => {
    const name = draft.name.trim();
    if (!name) {
      setError("태그 이름을 입력해 주세요.");
      return;
    }
    const patch = {};
    if (name !== tag.name) patch.name = name;
    if (draft.color !== tag.color) patch.color = draft.color;
    if (Object.keys(patch).length === 0) {
      setEditingId(null);
      return;
    }
    const updated = await withResult(() => updateSalesTag(tag.id, patch));
    if (updated) setEditingId(null);
  };

  const handleDelete = async (tag) => {
    const msg = tag.usage_count > 0
      ? `"${tag.name}" 태그를 삭제하면 사용 중인 결제 ${tag.usage_count}건이 "기타"로 옮겨집니다. 삭제할까요?`
      : `"${tag.name}" 태그를 삭제할까요?`;
    if (!window.confirm(msg)) return;
    await withResult(() => deleteSalesTag(tag.id));
  };

  const handleAlertOk = useCallback(() => {
    if (alert === "logout") navigate("/");
  }, [alert, navigate]);

  return (
    <>
      <div style={styles.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) finish(); }}>
        <div style={styles.modal} role="dialog" aria-label="매출 태그 관리">
          <div style={styles.header}>
            <span>매출 태그 관리</span>
            <button type="button" aria-label="닫기" style={styles.closeBtn} onClick={finish}><FaTimes size={16} /></button>
          </div>
          <div style={styles.hint}>
            결제 1건에는 태그 1개만 붙습니다. 기본 태그는 삭제할 수 없고, 사용 중인 태그를 삭제하면 해당 결제는 "기타"로 옮겨집니다.
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <div style={styles.list}>
            {isLoading ? (
              <div style={styles.loading}>로딩 중...</div>
            ) : tags.map((tag) => {
              const editing = editingId === tag.id;
              return (
                <div key={tag.id} style={styles.row} data-testid={`tag-row-${tag.id}`}>
                  {editing ? (
                    <input
                      type="color"
                      aria-label={`${tag.name} 색`}
                      style={styles.colorInput}
                      value={draft.color}
                      onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
                    />
                  ) : (
                    <span style={{ width: "18px", height: "18px", borderRadius: "5px", backgroundColor: tag.color, display: "inline-block" }} />
                  )}
                  <div>
                    {editing ? (
                      <input
                        aria-label={`${tag.name} 이름`}
                        style={{ ...styles.nameInput, ...styles.nameInputEditing }}
                        value={draft.name}
                        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(tag); if (e.key === "Escape") setEditingId(null); }}
                      />
                    ) : (
                      <span style={{ fontWeight: "600", color: "#1f2937" }}>
                        {tag.name}
                        {tag.is_default && <span style={styles.lock}>기본</span>}
                      </span>
                    )}
                  </div>
                  <div style={styles.usage}>{tag.usage_count.toLocaleString("ko-KR")}건</div>
                  <div style={styles.actions}>
                    {editing ? (
                      <>
                        <button type="button" style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => saveEdit(tag)}>저장</button>
                        <button type="button" style={styles.btn} onClick={() => setEditingId(null)}>취소</button>
                      </>
                    ) : (
                      <>
                        <button type="button" style={styles.btn} onClick={() => startEdit(tag)}>수정</button>
                        {!tag.is_default && (
                          <button type="button" style={{ ...styles.btn, ...styles.btnDanger }} onClick={() => handleDelete(tag)}>삭제</button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={styles.addRow}>
            <input
              type="color"
              aria-label="새 태그 색"
              style={styles.colorInput}
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
            />
            <input
              aria-label="새 태그 이름"
              style={styles.addInput}
              placeholder="새 태그 이름 (예: 모두의 창업)"
              value={newName}
              maxLength={20}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            />
            <button type="button" style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleAdd}>추가</button>
          </div>
        </div>
      </div>

      {alertBox.bool && (
        <CommonModal setModal={setAlertBox} modal={alertBox} okFn={handleAlertOk} />
      )}
    </>
  );
};

export default SalesTagManageModal;
